import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  TextField,
  Typography,
  Alert,
} from '@one-thd/sui-atomic-components';
import { AddToContext } from '../../AddToContext';
import { SCREENS } from '../../AddToConstants';

const computeDelta = (prefilledAnswer, finalAnswer) => {
  const hadPrefill = prefilledAnswer !== null && prefilledAnswer !== '';
  const hasAnswer = finalAnswer !== null && finalAnswer !== '';

  if (!hadPrefill && hasAnswer) return 'added';
  if (hadPrefill && hasAnswer && prefilledAnswer !== finalAnswer) return 'changed';
  if (hadPrefill && hasAnswer && prefilledAnswer === finalAnswer) return 'confirmed';
  if (hadPrefill && !hasAnswer) return 'cleared';
  return 'skipped';
};

const SHOWER_TUB_QUESTION_ID = 'shower_tub';

export const BathroomQuestionnaire = ({
  name,
  materials,
  refType,
  refId,
}) => {
  const {
    setScreen,
    bathroomQuestionnaire,
    setPendingQuestionnaireRequest,
    questionnaireError,
    setQuestionnaireError,
    questionnaireAnswers,
    setQuestionnaireAnswers,
  } = useContext(AddToContext);

  const { questions, originalPrompt, inputType: savedInputType } = bathroomQuestionnaire || {};

  const [answers, setAnswers] = useState(() => {
    if (!questions) return {};

    // Restore answers from context if returning from an error
    if (questionnaireAnswers) {
      try {
        const saved = JSON.parse(questionnaireAnswers);
        return saved.reduce((acc, item) => {
          acc[item.id] = item.finalAnswer || '';
          return acc;
        }, {});
      } catch (parseError) { /* fall through to prefilled defaults */ }
    }

    return questions.reduce((acc, question) => {
      acc[question.id] = question.prefilledAnswer || '';
      return acc;
    }, {});
  });

  const existingMaterials = useMemo(() => {
    return (materials || []).map((material) => ({
      groupId: material.groupId,
      groupName: material.groupName,
      materials: (material.items || []).map((materialItem) => ({
        product: materialItem.name || '',
        brand: materialItem.brand || '',
        intent: materialItem.intent,
        quantity: materialItem.quantity || 1
      }))
    }));
  }, [materials]);

  const sortedQuestions = useMemo(() => {
    if (!questions) return [];
    return [...questions].sort((first, second) => (first.order || 0) - (second.order || 0));
  }, [questions]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    if (questionnaireError) setQuestionnaireError('');
  };

  const buildTemplateData = (action) => {
    const templateDataItems = [
      { key: 'materialsGroup', value: JSON.stringify(existingMaterials || []) },
      { key: 'title', value: name },
      { key: 'questionnaireAction', value: action },
      { key: 'bathroomQuestionsEnabled', value: 'true' },
    ];

    if (action === 'continue') {
      const questionnaireSubmittedAnswers = sortedQuestions.map((question) => ({
        id: question.id,
        label: question.label,
        prefilledAnswer: question.prefilledAnswer || null,
        finalAnswer: answers[question.id] || null,
        delta: computeDelta(question.prefilledAnswer || '', answers[question.id] || ''),
      }));
      templateDataItems.push({
        key: 'questionnaireAnswers',
        value: JSON.stringify(questionnaireSubmittedAnswers)
      });
    }

    return templateDataItems;
  };

  const handleSkip = () => {
    if (questionnaireError) setQuestionnaireError('');
    setQuestionnaireAnswers(null);
    setPendingQuestionnaireRequest({
      message: originalPrompt,
      templateData: buildTemplateData('skip'),
      inputType: savedInputType || 'TEXT',
      refType,
      refId,
    });
    setScreen(SCREENS.AI_LOADING_ANIMATION);
  };

  const handleContinue = () => {
    if (questionnaireError) setQuestionnaireError('');
    const templateData = buildTemplateData('continue');
    const answersJson = templateData.find((item) => item.key === 'questionnaireAnswers')?.value;
    setQuestionnaireAnswers(answersJson || null);
    setPendingQuestionnaireRequest({
      message: originalPrompt,
      templateData,
      inputType: savedInputType || 'TEXT',
      refType,
      refId,
    });
    setScreen(SCREENS.AI_LOADING_ANIMATION);
  };

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <>
      <DrawerBody>
        <div className="sui-flex sui-flex-col sui-gap-4 sui-py-2">
          <Typography variant="body-base" weight="bold">
            Add a few more details for a better list!
          </Typography>

          {questionnaireError && (
            <Alert status="error">{questionnaireError}</Alert>
          )}

          {sortedQuestions.map((question) => (
            <div key={question.id} data-testid={`question-${question.id}`}>
              <TextField
                label={question.label}
                placeholder={question.placeholder || ''}
                helpMessage={question.helperText || ''}
                value={answers[question.id] || ''}
                onChange={(event) => handleAnswerChange(question.id, event.target.value)}
                fullWidth
                multiline={question.id === SHOWER_TUB_QUESTION_ID}
                minRows={question.id === SHOWER_TUB_QUESTION_ID ? 2 : undefined}
                maxRows={question.id === SHOWER_TUB_QUESTION_ID ? 3 : undefined}
              />
            </div>
          ))}
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div className="sui-flex sui-gap-2 sui-w-full">
          <Button
            data-testid="questionnaire-skip-button"
            variant="secondary"
            onClick={handleSkip}
            fullWidth
          >
            Skip
          </Button>
          <Button
            data-testid="questionnaire-continue-button"
            variant="primary"
            onClick={handleContinue}
            fullWidth
          >
            Continue
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

BathroomQuestionnaire.displayName = 'BathroomQuestionnaire';

BathroomQuestionnaire.propTypes = {
  name: PropTypes.string,
  materials: PropTypes.arrayOf(PropTypes.shape({})),
  refType: PropTypes.string,
  refId: PropTypes.string,
};

BathroomQuestionnaire.defaultProps = {
  name: '',
  materials: [],
  refType: '',
  refId: '',
};
